import { produce } from 'immer'
import { BulkVirtualCardJobStatus } from 'src/models/virtualCard.models'
import {
  cspIssuersQueryKeys,
  ORG_EXPENSE_REPORTS_QK,
  ORG_USAGES_QK,
  transactionsQueryKeys,
  userQueryKeys,
  virtualCardsQueryKeys,
} from 'src/queries/queries.constants'
import { queryClient } from 'src/queryClient'
import {
  GET_CSP_ME_SUCCESS,
  GET_ME_SUCCESS,
  POST_CSP_LOGIN_ISSUER_INFO_SUCCESS,
  POST_CSP_LOGIN_SUCCESS,
} from 'src/redux/actions/auth.actions'
import { GET_ASYNC_TRANSACTION_UPDATE_JOB_STATUS_COMPLETE } from 'src/redux/actions/data/transactions.actions'
import {
  PATCH_USER_SUCCESS,
  POST_USER_AVATAR_SUCCESS,
} from 'src/redux/actions/data/users.actions'
import {
  GET_BULK_VIRTUAL_CARD_JOB_STATUS_SUCCESS,
  GET_VIRTUAL_CARD_SUCCESS,
  POST_VIRTUAL_CARD_SUCCESS,
  PUT_CLOSE_VIRTUAL_CARD_SUCCESS,
  PUT_VIRTUAL_CARD_CANCEL_SUCCESS,
  PUT_VIRTUAL_CARD_CANCEL_UPDATE_SUCCESS,
  PUT_VIRTUAL_CARD_REJECT_SUCCESS,
  PUT_VIRTUAL_CARD_SUCCESS,
} from 'src/redux/actions/data/virtualCards.actions'
import { Actions } from 'src/redux/reducers/data.reducer'

const ReactQueryInteropReducer = (
  state: Record<string, never> = {},
  action: Actions,
): Record<string, never> => {
  return produce(state, (_) => {
    switch (action.type) {
      case PUT_VIRTUAL_CARD_CANCEL_SUCCESS:
      case PUT_CLOSE_VIRTUAL_CARD_SUCCESS:
      case PUT_VIRTUAL_CARD_CANCEL_UPDATE_SUCCESS:
      case PUT_VIRTUAL_CARD_REJECT_SUCCESS:
      case PUT_VIRTUAL_CARD_SUCCESS:
      case POST_VIRTUAL_CARD_SUCCESS: {
        // TODO: we will need to save the resulting virtual card to the query store manually
        // because of timing issues with SpiceDB when attempting to immediately refetch paginated views
        queryClient.invalidateQueries({ queryKey: virtualCardsQueryKeys.all })
        // Active virtual-card count drives SaaS limit gating — refetch after any
        // VC create/close/cancel so the downgrade meter sees the latest count.
        // Only invalidate when an org-usages query is already cached (i.e. the
        // user is on a SaaS-enabled brand and a consumer has mounted it).
        // Without this gate, non-SaaS brands trigger a wasted /saas/{orgId}/usages
        // refetch (the endpoint 404s) on every VC mutation app-wide.
        const usagesAlreadyCached = queryClient
          .getQueryCache()
          .findAll({
            predicate: (q) => q.queryKey.includes(ORG_USAGES_QK),
          })
          .some((q) => q.state.data !== undefined)
        if (usagesAlreadyCached) {
          queryClient.invalidateQueries({
            predicate: (query) => query.queryKey.includes(ORG_USAGES_QK),
          })
        }
        break
      }
      case GET_BULK_VIRTUAL_CARD_JOB_STATUS_SUCCESS: {
        if (action.payload.status === BulkVirtualCardJobStatus.Completed) {
          queryClient.invalidateQueries({ queryKey: virtualCardsQueryKeys.all })
        }
        break
      }
      case GET_VIRTUAL_CARD_SUCCESS: {
        // The detail surface now reads the card/permissions from React Query, so
        // the legacy getVirtualCard refresh (Pusher VirtualCard events,
        // sanctioned-screening completion) must invalidate the RQ detail cache —
        // otherwise the mounted detail page renders stale state until a hard
        // refresh. Skip when the action came from the detail query's own
        // onSuccess to avoid a refetch loop. Invalidating detail(id) also covers
        // its permissions/revisions queries (they share the detail key prefix).
        if (action.meta?.source !== 'react-query') {
          const virtualCardId = action.payload?.virtualCard?.id
          if (virtualCardId) {
            queryClient.invalidateQueries({
              queryKey: virtualCardsQueryKeys.detail(virtualCardId),
            })
          }
        }
        break
      }
      case PATCH_USER_SUCCESS: {
        queryClient.invalidateQueries({ queryKey: userQueryKeys.all })
        break
      }
      case POST_USER_AVATAR_SUCCESS: {
        queryClient.invalidateQueries({ queryKey: userQueryKeys.me() })
        break
      }
      case GET_ME_SUCCESS: {
        // Only invalidate React Query cache when the action comes from Redux
        // (not from React Query's own onSuccess callback, which already has fresh data)
        if (action.meta?.source !== 'react-query') {
          queryClient.invalidateQueries({ queryKey: userQueryKeys.me() })
        }
        break
      }
      case GET_CSP_ME_SUCCESS: {
        queryClient.invalidateQueries({ queryKey: userQueryKeys.me() })
        // Seed (not invalidate) the CSP issuers cache from the thunk's
        // payload: the getCspMe thunk remains the auth-boundary fetcher
        // (SSR seed, App.tsx CSP-auth effect) and seeding preserves the
        // legacy single-fetch behavior for state.data.issuers readers.
        queryClient.setQueryData(cspIssuersQueryKeys.me(), action.payload)
        break
      }
      case POST_CSP_LOGIN_SUCCESS:
      case POST_CSP_LOGIN_ISSUER_INFO_SUCCESS: {
        // The login payload embeds the same user+issuers as /csp/me; seeding
        // avoids an extra /csp/me fetch the moment a CSP-gated query mounts.
        queryClient.setQueryData(cspIssuersQueryKeys.me(), {
          user: action.payload.user,
          issuers: action.payload.issuers,
        })
        break
      }
      case GET_ASYNC_TRANSACTION_UPDATE_JOB_STATUS_COMPLETE: {
        // must refresh all data when a bulk update completes since the data isnt returned
        // and there is no way to know what was updated
        queryClient.invalidateQueries({ queryKey: [ORG_EXPENSE_REPORTS_QK] })
        queryClient.invalidateQueries({ queryKey: transactionsQueryKeys.all })
        break
      }
    }
  })
}

export default ReactQueryInteropReducer
